/* eslint-disable react-refresh/only-export-components */
import type {
  SailPlotExtensionComponent,
  SailPlotExtensionContext,
} from "@heiselanalytics/sailplot";
import { heiselLegalOperator, type LegalOperator } from "../config/legal";
import { PageActions } from "./PageActions";

interface LegalPageContext {
  customerImprintUrl?: string;
  hostedForCustomer: boolean;
  operator: LegalOperator;
}

const copy = {
  en: {
    eyebrow: "Legal",
    title: "Legal Notice",
    intro: "Provider information pursuant to Section 5 DDG.",
    introHosted:
      "Provider information for the SailPlot platform and its technical operation.",
    provider: "Provider of this instance",
    platformProvider: "SailPlot provider and technical hosting",
    contact: "Contact",
    register: "Register information",
    vatId: "VAT identification number",
    responsibility: "Responsibility for content",
    customerResponsibility:
      "The operator of this branded instance remains responsible for the content, links, logos, trademarks, and information it supplies. Heisel Analytics does not adopt operator-provided content as its own.",
    customerImprint: "Open the operator's provider information",
    ownResponsibility:
      "The provider named above operates this website and is responsible for its content.",
    hosting: "Technical implementation and hosting",
    hostingText:
      "Heisel Analytics provides the technical implementation and hosts the adapted open-source SailPlot application. Heisel Analytics does not adopt operator-provided content as its own.",
    openSource: "Open-source software",
    openSourceText:
      "SailPlot is open-source software under the GNU General Public License version 3. Source code, license, and attribution are available on the Open Source page.",
    openSourceAction: "Open source and license",
    updated: "Last updated: 14 August 2026",
  },
  de: {
    eyebrow: "Rechtliches",
    title: "Impressum",
    intro: "Anbieterinformationen gemäß § 5 DDG.",
    introHosted:
      "Anbieterinformationen zur SailPlot-Plattform und ihrem technischen Betrieb.",
    provider: "Anbieter dieser Instanz",
    platformProvider: "SailPlot-Anbieter und technisches Hosting",
    contact: "Kontakt",
    register: "Registerangaben",
    vatId: "Umsatzsteuer-Identifikationsnummer",
    responsibility: "Verantwortlichkeit für Inhalte",
    customerResponsibility:
      "Der Betreiber dieser gebrandeten Instanz bleibt für die von ihm bereitgestellten Inhalte, Links, Logos, Marken und Angaben verantwortlich. Heisel Analytics macht sich vom Betreiber bereitgestellte Inhalte nicht zu eigen.",
    customerImprint: "Anbieterinformationen des Betreibers öffnen",
    ownResponsibility:
      "Der oben genannte Anbieter betreibt diese Website und ist für ihre Inhalte verantwortlich.",
    hosting: "Technische Umsetzung und Hosting",
    hostingText:
      "Heisel Analytics übernimmt die technische Umsetzung und hostet die angepasste Open-Source-Anwendung SailPlot. Heisel Analytics macht sich vom Betreiber bereitgestellte Inhalte nicht zu eigen.",
    openSource: "Open-Source-Software",
    openSourceText:
      "SailPlot ist Open-Source-Software unter der GNU General Public License Version 3. Quellcode, Lizenz und Herkunftsnachweis sind auf der Open-Source-Seite verfügbar.",
    openSourceAction: "Quellcode und Lizenz öffnen",
    updated: "Stand: 14. August 2026",
  },
} as const;

function OperatorAddress({ operator }: { operator: LegalOperator }) {
  return (
    <address className="platform-legal-address">
      <strong>{operator.tradingName ?? operator.legalName}</strong>
      {operator.tradingName && <span>{operator.legalName}</span>}
      {operator.addressLines.map((line) => (
        <span key={line}>{line}</span>
      ))}
      <span>{operator.country}</span>
    </address>
  );
}

function ImprintPage({
  language,
  navigate,
  customerImprintUrl,
  hostedForCustomer,
  operator,
}: SailPlotExtensionContext & LegalPageContext) {
  const text = copy[language];
  const hostIsOperator = operator.email === heiselLegalOperator.email;

  return (
    <article className="platform-page platform-reference-page platform-legal-page">
      <header>
        <span className="platform-page-eyebrow">{text.eyebrow}</span>
        <h1>{text.title}</h1>
        <p>{hostedForCustomer ? text.introHosted : text.intro}</p>
      </header>

      <div className="platform-legal-sections">
        <section>
          <h2>{hostedForCustomer ? text.platformProvider : text.provider}</h2>
          <OperatorAddress operator={operator} />
        </section>

        <section>
          <h2>{text.contact}</h2>
          <p>
            <a href={`mailto:${operator.email}`}>{operator.email}</a>
            {operator.phone && (
              <>
                <br />
                <a href={`tel:${operator.phone.replace(/\s/gu, "")}`}>
                  {operator.phone}
                </a>
              </>
            )}
          </p>
          {operator.register && (
            <p>
              <strong>{text.register}:</strong> {operator.register}
            </p>
          )}
          {operator.vatId && (
            <p>
              <strong>{text.vatId}:</strong> {operator.vatId}
            </p>
          )}
        </section>

        <section>
          <h2>{text.responsibility}</h2>
          <p>
            {hostedForCustomer
              ? text.customerResponsibility
              : text.ownResponsibility}
          </p>
          {hostedForCustomer && customerImprintUrl && (
            <p className="platform-legal-external-links">
              <a
                href={customerImprintUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {text.customerImprint} ↗
              </a>
            </p>
          )}
        </section>

        {!hostIsOperator && (
          <section>
            <h2>{text.hosting}</h2>
            <OperatorAddress operator={heiselLegalOperator} />
            <p>{text.hostingText}</p>
          </section>
        )}

        <section>
          <h2>{text.openSource}</h2>
          <p>{text.openSourceText}</p>
          <button
            type="button"
            className="platform-legal-link-button"
            onClick={() => navigate("/open-source")}
          >
            {text.openSourceAction}
          </button>
        </section>
      </div>

      <small className="platform-legal-updated">{text.updated}</small>
      <PageActions language={language} navigate={navigate} />
    </article>
  );
}

export const createImprintPage = (
  legalContext: LegalPageContext,
): SailPlotExtensionComponent =>
  function TenantImprintPage(context) {
    return <ImprintPage {...context} {...legalContext} />;
  };
